import { useCallback, useEffect, useState } from 'react';
import { fetchWithRetry } from '@/lib/apiRequest';

export interface ReportsSummary {
  totalClients: number;
  totalLeads: number;
  totalCases: number;
  totalTasks: number;
  totalGeneratedDocuments: number;
  totalCalendarEvents: number;
  totalLegalProducts: number;
  totalLegalSites: number;
  totalDocumentTemplates: number;
  totalMessageTemplates: number;
  totalKnowledgeItems: number;
}

export interface ReportsCountItem {
  label: string;
  count: number;
}

export interface ReportsRecentLead {
  id: string;
  name: string;
  source: string | null;
  status: string;
  priority: string;
  createdAt: string;
}

export interface ReportsRecentCase {
  id: string;
  title: string;
  clientName: string | null;
  legalArea: string | null;
  phase: string | null;
  status: string;
  priority: string;
  nextDeadlineAt: string | null;
  createdAt: string;
}

export interface ReportsRecentTask {
  id: string;
  title: string;
  status: string;
  priority: string;
  dueDate: string | null;
  responsibleUser: string | null;
  clientName: string | null;
  caseTitle: string | null;
  createdAt: string;
}

export interface ReportsPaymentItem {
  id: string;
  title: string;
  clientName: string | null;
  caseTitle: string | null;
  status: string;
  totalAmount: number;
  paidAmount: number;
  balanceAmount: number;
  firstDueDate: string | null;
  createdAt: string;
}

export interface ReportsInstallmentItem {
  id: string;
  paymentId: string;
  installmentNumber: number;
  amount: number;
  paidAmount: number;
  dueDate: string | null;
  status: string;
  paymentTitle: string;
  clientName: string | null;
  caseTitle: string | null;
  paymentStatus: string;
}

export interface ReportsData {
  summary: ReportsSummary;
  leads: {
    byStatus: ReportsCountItem[];
    bySource: ReportsCountItem[];
    recent: ReportsRecentLead[];
  };
  cases: {
    byStatus: ReportsCountItem[];
    byArea: ReportsCountItem[];
    byPhase: ReportsCountItem[];
    activeCount: number;
    archivedCount: number;
    recent: ReportsRecentCase[];
  };
  tasks: {
    byStatus: ReportsCountItem[];
    overdueCount: number;
    upcoming7DaysCount: number;
    completedCount: number;
    recent: ReportsRecentTask[];
  };
  finance: {
    totalPayments: number;
    totalContractedRevenue: number;
    totalReceivedRevenue: number;
    totalPendingRevenue: number;
    byStatus: ReportsCountItem[];
    overdueInstallmentsCount: number;
    overdueInstallmentsAmount: number;
    upcomingInstallmentsCount: number;
    upcomingInstallmentsAmount: number;
    recentPayments: ReportsPaymentItem[];
    overdueInstallments: ReportsInstallmentItem[];
    upcomingInstallments: ReportsInstallmentItem[];
  };
  productivity: {
    documentTemplates: number;
    generatedDocuments: number;
    messageTemplates: number;
    knowledgeItems: number;
    legalSites: number;
    calendarEvents: number;
  };
  generatedAt: string;
}

type UseReportsOptions = {
  autoLoad?: boolean;
};

type ReportsApiResponse = {
  success?: boolean;
  reports?: unknown;
  error?: string;
};

const nowIso = () => new Date().toISOString();

const emptyReports = (): ReportsData => ({
  summary: {
    totalClients: 0,
    totalLeads: 0,
    totalCases: 0,
    totalTasks: 0,
    totalGeneratedDocuments: 0,
    totalCalendarEvents: 0,
    totalLegalProducts: 0,
    totalLegalSites: 0,
    totalDocumentTemplates: 0,
    totalMessageTemplates: 0,
    totalKnowledgeItems: 0,
  },
  leads: { byStatus: [], bySource: [], recent: [] },
  cases: { byStatus: [], byArea: [], byPhase: [], activeCount: 0, archivedCount: 0, recent: [] },
  tasks: { byStatus: [], overdueCount: 0, upcoming7DaysCount: 0, completedCount: 0, recent: [] },
  finance: {
    totalPayments: 0,
    totalContractedRevenue: 0,
    totalReceivedRevenue: 0,
    totalPendingRevenue: 0,
    byStatus: [],
    overdueInstallmentsCount: 0,
    overdueInstallmentsAmount: 0,
    upcomingInstallmentsCount: 0,
    upcomingInstallmentsAmount: 0,
    recentPayments: [],
    overdueInstallments: [],
    upcomingInstallments: [],
  },
  productivity: {
    documentTemplates: 0,
    generatedDocuments: 0,
    messageTemplates: 0,
    knowledgeItems: 0,
    legalSites: 0,
    calendarEvents: 0,
  },
  generatedAt: nowIso(),
});

const toNumber = (value: unknown) => {
  const numeric = Number(value ?? 0);
  return Number.isFinite(numeric) ? numeric : 0;
};

const toText = (value: unknown, fallback = '') => {
  const text = String(value ?? '').trim();
  return text === '' ? fallback : text;
};

const mapCountItems = (items: unknown, keyName: string): ReportsCountItem[] => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.map((item: any) => ({
    label: toText(item?.[keyName], 'Sem classificação'),
    count: toNumber(item?.count),
  }));
};

const normalizeReports = (payload: ReportsApiResponse): ReportsData => {
  const reports = (payload.reports as any) ?? {};
  const summary = reports.summary ?? {};

  return {
    summary: {
      totalClients: toNumber(summary.totalClients),
      totalLeads: toNumber(summary.totalLeads),
      totalCases: toNumber(summary.totalCases),
      totalTasks: toNumber(summary.totalTasks),
      totalGeneratedDocuments: toNumber(summary.totalGeneratedDocuments),
      totalCalendarEvents: toNumber(summary.totalCalendarEvents),
      totalLegalProducts: toNumber(summary.totalLegalProducts),
      totalLegalSites: toNumber(summary.totalLegalSites),
      totalDocumentTemplates: toNumber(summary.totalDocumentTemplates),
      totalMessageTemplates: toNumber(summary.totalMessageTemplates),
      totalKnowledgeItems: toNumber(summary.totalKnowledgeItems),
    },
    leads: {
      byStatus: mapCountItems(reports.leads?.byStatus, 'status'),
      bySource: mapCountItems(reports.leads?.bySource, 'source'),
      recent: Array.isArray(reports.leads?.recent)
        ? reports.leads.recent.map((item: any) => ({
            id: String(item?.id ?? ''),
            name: toText(item?.name, 'Sem nome'),
            source: item?.source ? toText(item.source) : null,
            status: toText(item?.status, 'new'),
            priority: toText(item?.priority, 'normal'),
            createdAt: toText(item?.createdAt, nowIso()),
          }))
        : [],
    },
    cases: {
      byStatus: mapCountItems(reports.cases?.byStatus, 'status'),
      byArea: mapCountItems(reports.cases?.byArea, 'area'),
      byPhase: mapCountItems(reports.cases?.byPhase, 'phase'),
      activeCount: toNumber(reports.cases?.activeCount),
      archivedCount: toNumber(reports.cases?.archivedCount),
      recent: Array.isArray(reports.cases?.recent)
        ? reports.cases.recent.map((item: any) => ({
            id: String(item?.id ?? ''),
            title: toText(item?.title, 'Sem título'),
            clientName: item?.clientName ? toText(item.clientName) : null,
            legalArea: item?.legalArea ? toText(item.legalArea) : null,
            phase: item?.phase ? toText(item.phase) : null,
            status: toText(item?.status, 'active'),
            priority: toText(item?.priority, 'normal'),
            nextDeadlineAt: item?.nextDeadlineAt ? toText(item.nextDeadlineAt) : null,
            createdAt: toText(item?.createdAt, nowIso()),
          }))
        : [],
    },
    tasks: {
      byStatus: mapCountItems(reports.tasks?.byStatus, 'status'),
      overdueCount: toNumber(reports.tasks?.overdueCount),
      upcoming7DaysCount: toNumber(reports.tasks?.upcoming7DaysCount),
      completedCount: toNumber(reports.tasks?.completedCount),
      recent: Array.isArray(reports.tasks?.recent)
        ? reports.tasks.recent.map((item: any) => ({
            id: String(item?.id ?? ''),
            title: toText(item?.title, 'Sem título'),
            status: toText(item?.status, 'pending'),
            priority: toText(item?.priority, 'normal'),
            dueDate: item?.dueDate ? toText(item.dueDate) : null,
            responsibleUser: item?.responsibleUser ? toText(item.responsibleUser) : null,
            clientName: item?.clientName ? toText(item.clientName) : null,
            caseTitle: item?.caseTitle ? toText(item.caseTitle) : null,
            createdAt: toText(item?.createdAt, nowIso()),
          }))
        : [],
    },
    finance: {
      totalPayments: toNumber(reports.finance?.totalPayments),
      totalContractedRevenue: toNumber(reports.finance?.totalContractedRevenue),
      totalReceivedRevenue: toNumber(reports.finance?.totalReceivedRevenue),
      totalPendingRevenue: toNumber(reports.finance?.totalPendingRevenue),
      byStatus: mapCountItems(reports.finance?.byStatus, 'status'),
      overdueInstallmentsCount: toNumber(reports.finance?.overdueInstallmentsCount),
      overdueInstallmentsAmount: toNumber(reports.finance?.overdueInstallmentsAmount),
      upcomingInstallmentsCount: toNumber(reports.finance?.upcomingInstallmentsCount),
      upcomingInstallmentsAmount: toNumber(reports.finance?.upcomingInstallmentsAmount),
      recentPayments: Array.isArray(reports.finance?.recentPayments)
        ? reports.finance.recentPayments.map((item: any) => ({
            id: String(item?.id ?? ''),
            title: toText(item?.title, 'Sem título'),
            clientName: item?.clientName ? toText(item.clientName) : null,
            caseTitle: item?.caseTitle ? toText(item.caseTitle) : null,
            status: toText(item?.status, 'pending'),
            totalAmount: toNumber(item?.totalAmount),
            paidAmount: toNumber(item?.paidAmount),
            balanceAmount: toNumber(item?.balanceAmount),
            firstDueDate: item?.firstDueDate ? toText(item.firstDueDate) : null,
            createdAt: toText(item?.createdAt, nowIso()),
          }))
        : [],
      overdueInstallments: Array.isArray(reports.finance?.overdueInstallments)
        ? reports.finance.overdueInstallments.map((item: any) => ({
            id: String(item?.id ?? ''),
            paymentId: String(item?.paymentId ?? ''),
            installmentNumber: toNumber(item?.installmentNumber),
            amount: toNumber(item?.amount),
            paidAmount: toNumber(item?.paidAmount),
            dueDate: item?.dueDate ? toText(item.dueDate) : null,
            status: toText(item?.status, 'pending'),
            paymentTitle: toText(item?.paymentTitle, 'Sem título'),
            clientName: item?.clientName ? toText(item.clientName) : null,
            caseTitle: item?.caseTitle ? toText(item.caseTitle) : null,
            paymentStatus: toText(item?.paymentStatus, 'pending'),
          }))
        : [],
      upcomingInstallments: Array.isArray(reports.finance?.upcomingInstallments)
        ? reports.finance.upcomingInstallments.map((item: any) => ({
            id: String(item?.id ?? ''),
            paymentId: String(item?.paymentId ?? ''),
            installmentNumber: toNumber(item?.installmentNumber),
            amount: toNumber(item?.amount),
            paidAmount: toNumber(item?.paidAmount),
            dueDate: item?.dueDate ? toText(item.dueDate) : null,
            status: toText(item?.status, 'pending'),
            paymentTitle: toText(item?.paymentTitle, 'Sem título'),
            clientName: item?.clientName ? toText(item.clientName) : null,
            caseTitle: item?.caseTitle ? toText(item.caseTitle) : null,
            paymentStatus: toText(item?.paymentStatus, 'pending'),
          }))
        : [],
    },
    productivity: {
      documentTemplates: toNumber(reports.productivity?.documentTemplates),
      generatedDocuments: toNumber(reports.productivity?.generatedDocuments),
      messageTemplates: toNumber(reports.productivity?.messageTemplates),
      knowledgeItems: toNumber(reports.productivity?.knowledgeItems),
      legalSites: toNumber(reports.productivity?.legalSites),
      calendarEvents: toNumber(reports.productivity?.calendarEvents),
    },
    generatedAt: toText(reports.generatedAt, nowIso()),
  };
};

export function useReports(options: UseReportsOptions = {}) {
  const { autoLoad = true } = options;
  const [reports, setReports] = useState<ReportsData>(emptyReports());
  const [isLoadingReports, setIsLoadingReports] = useState(true);
  const [reportsError, setReportsError] = useState<string | null>(null);

  const loadReports = useCallback(async () => {
    setIsLoadingReports(true);
    setReportsError(null);

    try {
      const response = await fetchWithRetry('/api/reports');
      const data = (await response.json()) as ReportsApiResponse;

      if (!response.ok || !data.success || !data.reports) {
        throw new Error(data.error || `GET /api/reports falhou com status ${response.status}`);
      }

      setReports(normalizeReports(data));
    } catch (error) {
      console.error('Erro ao carregar relatórios reais.', error);
      setReports(emptyReports());
      setReportsError('Não foi possível carregar os relatórios reais.');
    } finally {
      setIsLoadingReports(false);
    }
  }, []);

  const refreshReports = useCallback(async () => {
    await loadReports();
  }, [loadReports]);

  useEffect(() => {
    if (!autoLoad) {
      setIsLoadingReports(false);
      return;
    }

    void loadReports();
  }, [autoLoad, loadReports]);

  return {
    reports,
    isLoadingReports,
    reportsError,
    loadReports,
    refreshReports,
    setReports,
  };
}
